import React, { useEffect, useMemo, useRef, useState } from "react";
import { GT_progressBarAndTasksGapInPx, GT_taskCardHeight } from "./index";
import {
  calculateWorkingDaysBetweenDates,
  getOrgWorkingDays,
  isOrgWeekend,
} from "../../utils/helperUtils";
import moment from "moment";
import { ViewType } from "./types";
import { useDispatch, useSelector } from "react-redux";
import {
  resetProjectIdForScroll,
  setProjectIdForScroll,
} from "../../redux/slices/timeline";
import Xarrow, { useXarrow } from "react-xarrows";
import { uuid } from "html-to-image/es/util";
import {
  DashboardStatus,
  dashboardStatusHexColors,
  getDashboardStatusLabel,
} from "../../constants/integrationTypes";
import { FiAlertTriangle } from "react-icons/fi";
import {
  getAcceleratingDependencies,
  getDependencyImpactValue,
  getImpactColorFromNumber,
  getImpactingDependencies,
  numberWithCommas,
} from "../../utils/taskUtils";
import { useJwt } from "../../helpers/common";
import DateShiftBadges from "./components/DateShiftBadges";
import { StrategyMapListItem } from "../../services/strategyMap";
import { FaGlasses } from "react-icons/fa";
import { EntityViewTooltip } from "../../features/EntityViewTooltip/EntityViewTooltip";

const TIMELINE_ANIMATION_MS = 600;

interface GTProjectProps {
  orgId: string;
  project: StrategyMapListItem;
  index: number;
  startDate: string;
  handleProjectClick: (task: any) => void;
  isTaskHighlighted: boolean;
  viewType: ViewType;
  spacing: number;
  simFlag: boolean;
  dependecyView: boolean;
}

const GTStrategyMap: React.FC<GTProjectProps> = ({
  orgId,
  project,
  index,
  handleProjectClick,
  isTaskHighlighted,
  startDate,
  viewType,
  spacing,
  simFlag,
  dependecyView,
}): React.ReactElement => {
  const updateXArrow = useXarrow();
  const dispatch = useDispatch();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    placement: "bottom",
    x: 0,
    y: 0,
  });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [isProjectHovered, setIsProjectHovered] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [depSweepProgress, setDepSweepProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const jwtData: any = useJwt();
  const { org_role: userRole = "" } = jwtData;

  const showCost = false;
  const simulation = simFlag;

  // Normalize backend data to be null-safe with sensible defaults
  const safeProject = useMemo(() => {
    const dependencies = Array.isArray(project?.dependencies)
      ? project?.dependencies
      : [];
    const start = project?.start_date || startDate;
    const end = project?.end_date || start;
    const simStart = project?.sim_data?.start_date || start;
    const simEnd = project?.sim_data?.end_date || end;
    return {
      project_id: project?.id ?? "",
      project_name: project?.name ?? "Untitled",
      stage: project?.stage ?? "-",
      status: project?.status ?? "",
      is_sim_pending: project?.is_sim_pending ?? false,
      inCriticalPath: Boolean(project?.isCritical),
      progress: Number(project?.progress ?? 0),
      start_date: start,
      entity_type: project?.entity_type,
      integration_data: project?.integration_data,
      end_date: end,
      sim_data: {
        start_date: simStart,
        end_date: simEnd,
        project_cost: Number(project?.sim_data?.project_costs?.total_cost ?? 0),
        status: project?.sim_data?.status ?? "",
      },
      project_cost: Number(project?.project_costs?.total_cost ?? 0),
      dependencies,
    } as const;
  }, [project, startDate]);

  const pStartDate = simulation
    ? safeProject.sim_data.start_date
    : safeProject.start_date;
  const pEndDate = simulation
    ? safeProject.sim_data.end_date
    : safeProject.end_date;

  const { orgSchedule: organizationHolidays = [] } = useSelector(
    (state: any) => state.organization,
  );

const activeTab = dependecyView ? "dependencies" : "timeline";
  const organizationWorkingDays = getOrgWorkingDays(orgId);

  // Calculate position based on working days between project start and task start
  const calculateWorkingDaysOffset = () => {
    if (viewType === "weekly") {
      const adjustedStartDate = moment(startDate)
        .subtract(2, "days")
        .startOf("day");
      let taskStartMoment = moment(pStartDate).startOf("day");

      // If task starts on a weekend or holiday, move it to next working day
      while (true) {
        const currentDate = taskStartMoment.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) =>
            (h?.date || "").split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          break;
        }
        taskStartMoment.add(1, "day");
      }

      // Count non-weekend and non-holiday days between adjusted start and task start
      let workingDays = 0;
      const current = moment(adjustedStartDate);

      while (current.isSameOrBefore(taskStartMoment)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) =>
            (h?.date || "").split("T")[0] === currentDate,
        );
        if (!isOrgWeekend(currentDate, organizationWorkingDays) && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to fix off-by-one
    }

    if (viewType === "monthly") {
      let taskStartMoment = moment(pStartDate).startOf("day");
      const baselineStart = moment(startDate)
        .subtract(7, "days")
        .startOf("day");

      // If task starts on a weekend or holiday, move it to next working day
      while (true) {
        const currentDate = taskStartMoment.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) =>
            (h?.date || "").split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          break;
        }
        taskStartMoment.add(1, "day");
      }

      // Count working days from the start of the month
      let workingDays = 0;
      const current = moment(baselineStart);

      while (current.isSameOrBefore(taskStartMoment)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );
        if (!isOrgWeekend(currentDate, organizationWorkingDays) && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to fix off-by-one
    }

    if (viewType === "yearly") {
      // For yearly view, calculate total days between start and end
      const taskStart = moment(pStartDate);
      const taskEnd = moment(pEndDate);
      const taskYearStart = moment(taskStart).startOf("year");

      // Calculate days from start of task's year
      const startDayOfYear = taskStart.diff(taskYearStart, "days");
      const endDayOfYear = taskEnd.diff(taskYearStart, "days");
      const taskDuration = endDayOfYear - startDayOfYear + 1; // +1 to include both start and end days

      // Position based on year offset and days within the year
      return { length: taskDuration };
    }

    // For quarterly view
    if (viewType === "quarterly") {
      const taskStart = moment(pStartDate).startOf("day");
      const projectStartMonth = moment(startDate).startOf("month");

      // Count working days from the start of the month
      let workingDays = 0;
      const current = moment(projectStartMonth);

      while (current.isSameOrBefore(taskStart)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) =>
            (h?.date || "").split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to align with grid
    }

    // For other views, use the default calculation
    const workingDays = calculateWorkingDaysBetweenDates(
      startDate,
      pStartDate,
      organizationWorkingDays,
      organizationHolidays,
    );
    return { length: workingDays };
  };

  const calculateTaskWidth = () => {
    const workingDays = calculateWorkingDaysBetweenDates(
      pStartDate,
      pEndDate,
      organizationWorkingDays,
      organizationHolidays,
    );
    return { length: workingDays };
  };

  const workingDaysToStart = calculateWorkingDaysOffset();
  const workingDaysInTask = calculateTaskWidth();

  // Initialize position variables
  let taskPosLeftInPx = 0;
  let taskWidth = 0;

  if (viewType === "yearly") {
    // Align with YearlyGridView: baseline is Jan 1 or Jul 1 of the start year
    const taskStart = moment(pStartDate).startOf("day");
    const taskEnd = moment(pEndDate).startOf("day");
    const rawStart = moment(startDate);
    const baselineStart =
      rawStart.month() >= 6
        ? rawStart.clone().startOf("year").add(6, "months")
        : rawStart.clone().startOf("year");

    const offsetDays = Math.max(0, taskStart.diff(baselineStart, "days"));
    const taskDuration = taskEnd.diff(taskStart, "days") + 1; // include both start and end

    taskPosLeftInPx = Math.floor(offsetDays * spacing);
    taskWidth = Math.floor(taskDuration * spacing);
  } else {
    // For other views, use pixel-based positioning
    taskPosLeftInPx = Math.floor(workingDaysToStart.length * spacing);
    taskWidth = Math.floor(workingDaysInTask.length * spacing);
  }

  // Overlays are always shown in simulation mode
  const prevViewTypeRef = useRef<ViewType>(viewType);
  const isViewSwitch = prevViewTypeRef.current !== viewType;
  useEffect(() => {
    prevViewTypeRef.current = viewType;
  }, [viewType]);

  const impactingDependencies = getImpactingDependencies(
    safeProject.dependencies,
    simFlag,
  );
  const hasPositiveImpact = impactingDependencies.length > 0;

  const acceleratingDependencies = useMemo(
    () => getAcceleratingDependencies(safeProject.dependencies || [], simFlag),
    [safeProject.dependencies, simFlag],
  );
  const hasNegativeImpact = acceleratingDependencies.length > 0;

  const calculateTooltipPosition = (event?: MouseEvent) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tooltip dimensions (approximate)
    const tooltipWidth = 300;
    const tooltipHeight = 200; // Approximate height
    const margin = 16; // Margin from viewport edges
    const sidebarElementGlobal = document.getElementById("roadmap-sidebar");
    const sidebarRightEdgeGlobal = sidebarElementGlobal
      ? sidebarElementGlobal.getBoundingClientRect().right
      : 0;

    let placement = "top"; // Default to top (above the bar)
    let x = 0;

    // Get cursor position if event is provided
    if (event) {
      // Calculate relative position within the container
      x = event.clientX - containerRect.left;

      // Ensure tooltip doesn't go off-screen horizontally and not cross sidebar edge
      const tooltipLeft = containerRect.left + x - tooltipWidth / 2;
      const tooltipRight = tooltipLeft + tooltipWidth;

      // Calculate minimum allowed left considering viewport margin and sidebar edge
      const minAllowedLeft = Math.max(margin, sidebarRightEdgeGlobal + margin);

      if (tooltipLeft < minAllowedLeft) {
        x = minAllowedLeft - containerRect.left + tooltipWidth / 2;
      } else if (tooltipRight > viewportWidth - margin) {
        // Clamp right side
        x =
          viewportWidth -
          margin -
          tooltipWidth -
          containerRect.left +
          tooltipWidth / 2;
      }
    } else {
      // Fallback to center if no event
      x = containerRect.width / 2;
      // Clamp to ensure does not cross sidebar or viewport when centered
      const centeredTooltipLeft = containerRect.left + x - tooltipWidth / 2;
      const centeredTooltipRight = centeredTooltipLeft + tooltipWidth;
      const minAllowedLeftFallback = Math.max(
        margin,
        sidebarRightEdgeGlobal + margin,
      );
      if (centeredTooltipLeft < minAllowedLeftFallback) {
        x = minAllowedLeftFallback - containerRect.left + tooltipWidth / 2;
      } else if (centeredTooltipRight > viewportWidth - margin) {
        x =
          viewportWidth -
          margin -
          tooltipWidth -
          containerRect.left +
          tooltipWidth / 2;
      }
    }

    // Check space above (preferred for tooltips)
    const spaceAbove = containerRect.top;
    const spaceBelow = viewportHeight - containerRect.bottom;

    // Check if tooltip would be overlapped by timeline navbar or other UI elements
    const safeTopZone = 400; // Very conservative estimate to avoid all clipping
    const tooltipTopPosition = containerRect.top - tooltipHeight - margin;

    // Force tooltips below for upper portion of screen to avoid any clipping
    // Check multiple conditions: tooltip position, container position, and upper screen area
    if (
      tooltipTopPosition < safeTopZone ||
      containerRect.top < safeTopZone ||
      containerRect.top < viewportHeight * 0.3
    ) {
      // Upper 30% of screen
      placement = "bottom";
    } else if (spaceAbove >= tooltipHeight + margin) {
      placement = "top";
    } else if (spaceBelow >= tooltipHeight + margin) {
      placement = "bottom";
    } else {
      // Not enough space above or below, use the side with more space
      placement = spaceAbove > spaceBelow ? "top" : "bottom";
    }

    setTooltipPosition({ placement, x, y: 0 });
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    // Clear any existing hide timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Clear any existing show timer to prevent multiple timers
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    // Set timer to show tooltip after 500ms
    showTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(() => {
        calculateTooltipPosition(event.nativeEvent);
      }, 0);
      showTimerRef.current = null;
    }, 500);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (showTooltip && !isTooltipHovered) {
      calculateTooltipPosition(event.nativeEvent);
    }
  };

  const handleMouseLeave = () => {
    // Clear any existing show timer
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    // Clear any existing hide timer to prevent multiple timers
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Set timer to hide tooltip after 200ms
    hideTimerRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsTooltipHovered(false);
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    const handleResize = () => {
      if (showTooltip) {
        calculateTooltipPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      // Clean up timers on unmount
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [showTooltip]);

  useEffect(() => {
    updateXArrow();
  }, [viewType]);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem(null), 2000); // Reset after 2 seconds
      console.log(`Copied ${type}: ${text}`);
    } catch (err) {
      console.error("Failed to copy: ", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem(null), 2000); // Reset after 2 seconds
    }
  };

  const truncateMiddle = (str: string, maxLength: number = 20) => {
    if (str.length <= maxLength) return str;
    const start = Math.ceil(maxLength / 3);
    const end = Math.floor(maxLength / 3);
    return `${str.substring(0, start)}...${str.substring(str.length - end)}`;
  };

  // Convert plural childEntityType to singular using major English pluralization rules
  const getSingularEntityType = (entityType: string) => {
    if (!entityType) return "Entity";

    const lower = entityType.toLowerCase();

    // Special case: Tasks should become User Story
    if (lower === "tasks") {
      return "User Story";
    }

    // Words ending in -ies (e.g., Stories -> Story, Companies -> Company)
    if (lower.endsWith("ies")) {
      return entityType.slice(0, -3) + "y";
    }

    // Words ending in -es after s, x, z, ch, sh (e.g., Boxes -> Box, Dishes -> Dish)
    if (
      lower.endsWith("es") &&
      (lower.endsWith("ses") ||
        lower.endsWith("xes") ||
        lower.endsWith("zes") ||
        lower.endsWith("ches") ||
        lower.endsWith("shes"))
    ) {
      return entityType.slice(0, -2);
    }

    // Words ending in -ves (e.g., Lives -> Life, Knives -> Knife)
    if (lower.endsWith("ves")) {
      return entityType.slice(0, -3) + "fe";
    }

    // Words ending in -s but not -ss (e.g., Projects -> Project, but not Glass -> Glas)
    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return entityType.slice(0, -1);
    }

    // Return as-is if no plural pattern matches
    return entityType;
  };

  const curveness = useMemo(() => {
    switch (viewType) {
      case "yearly":
        return 1.08;

      case "quarterly":
        return 1.08;

      case "monthly":
        return 1.02;

      case "weekly":
        return 1.02;

      default:
        return undefined;
    }
  }, [viewType]);

  const status =
    (simulation
      ? (safeProject.sim_data?.status ?? safeProject.status)
      : safeProject.status) || "";
  const statusKey = status.toLowerCase();
  const statusLabel = getDashboardStatusLabel(statusKey);
  const statusHexColor = dashboardStatusHexColors[statusKey] || "#a3a3a3";
  const showRiskTriangle =
    statusKey === DashboardStatus.DELAYED ||
    statusKey === DashboardStatus.UNALLOCATED ||
    statusKey === DashboardStatus.BLOCKED;

  const signedDependencyImpactDays = useMemo(() => {
    if (
      !Array.isArray(safeProject.dependencies) ||
      safeProject.dependencies.length === 0
    )
      return 0;
    return safeProject.dependencies.reduce((acc: number, dep: any) => {
      const val = simFlag ? dep?.sim_impact : dep?.impact;
      if (typeof val === "number" && !isNaN(val)) {
        return acc + val;
      }
      return acc;
    }, 0);
  }, [safeProject.dependencies, simFlag]);

  const dependencyShiftPx = Math.round(
    (activeTab === "dependencies" ? signedDependencyImpactDays : 0) * spacing,
  );

  const prevActiveTabRef = useRef<string | null>(activeTab);
  const isEnteringDependencies =
    activeTab === "dependencies" && prevActiveTabRef.current !== "dependencies";
  useEffect(() => {
    prevActiveTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    let raf = 0 as number | undefined;
    if (activeTab === "dependencies") {
      const start = performance.now();
      const run = (now: number) => {
        updateXArrow();
        const p = Math.min(1, (now - start) / TIMELINE_ANIMATION_MS);
        setDepSweepProgress(p);
        if (p < 1) raf = requestAnimationFrame(run);
      };
      setDepSweepProgress(0);
      raf = requestAnimationFrame(run);
    } else {
      updateXArrow();
      setDepSweepProgress(0);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [activeTab, dependencyShiftPx]);

  useEffect(() => {
    let raf = 0 as number | undefined;
    const start = performance.now();
    const duration = TIMELINE_ANIMATION_MS; // matches sim/live transitions
    const run = (now: number) => {
      updateXArrow();
      const p = Math.min(1, (now - start) / duration);
      if (p < 1) raf = requestAnimationFrame(run);
    };
    raf = requestAnimationFrame(run);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [simulation, taskPosLeftInPx, taskWidth]);

  return (
    <>
      <span></span>
      <div style={{ height: GT_taskCardHeight }}>
        <button onClick={() => handleProjectClick(project)}>
          <div
            id={`project-${safeProject.project_id}`}
            className={`flex absolute bg-white dark:bg-[#1B263B] cursor-pointer border-l-4 border-r-2 border-t-2 border-b-2 rounded-md
            ${
              activeTab === "dependencies" && hasPositiveImpact
                ? "border-t-red-500 border-r-red-500 border-b-red-500 dark:border-t-red-400 dark:border-r-red-400 dark:border-b-red-400"
                : "border-r-gray-300 dark:border-r-[#394256] border-b-gray-300 dark:border-b-[#394256] border-t-gray-300 dark:border-t-[#394256]"
            } hover:bg-gray-50 dark:hover:bg-[#212e47]
            hover:border-l-6 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_0_0_1px_rgba(99,102,241,0.1),0_0_8px_rgba(99,102,241,0.2)] dark:hover:shadow-[0_8px_16px_rgba(0,0,0,0.5),0_0_0_1px_rgba(99,102,241,0.1),0_0_8px_rgba(99,102,241,0.2)]`}
            style={{
              top: index * GT_taskCardHeight,
              left: taskPosLeftInPx + dependencyShiftPx,
              borderLeftColor: statusHexColor,
              zIndex: showTooltip || isProjectHovered ? 5 : 2,
              borderTopColor: safeProject.inCriticalPath
                ? "rgb(182, 117, 117)"
                : undefined,
              borderTopWidth: safeProject.inCriticalPath ? 3 : 2,
              width: taskWidth,
              height: GT_taskCardHeight - GT_progressBarAndTasksGapInPx,
              boxShadow: isTaskHighlighted
                ? "0 0 0 1px #FFD70066, 0 0 12px 2px #FFD700"
                : "",
              transition: (() => {
                const transitions: string[] = [];
                if (!isViewSwitch) {
                  transitions.push(
                    `left ${TIMELINE_ANIMATION_MS}ms ease-out`,
                    `width ${TIMELINE_ANIMATION_MS}ms ease-out`,
                  );
                }
                if (isEnteringDependencies) {
                  transitions.push(`left ${TIMELINE_ANIMATION_MS}ms ease-out`);
                }
                return transitions.length ? transitions.join(", ") : "none";
              })(),
              willChange: "left, width",
              overflow: "visible",
            }}
            ref={containerRef}
            onMouseEnter={(e) => {
              setIsProjectHovered(true);
              handleMouseEnter(e);
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
              setIsProjectHovered(false);
              handleMouseLeave();
            }}
          >
            {/* Overlay red background only when dependencies are impacting */}
            {activeTab === "dependencies" && hasPositiveImpact && (
              <div className="absolute transparent top-0 left-0 w-full h-full bg-red-500/10 dark:bg-red-600/20 rounded pointer-events-none z-[1]" />
            )}
            {activeTab === "dependencies" &&
              !hasPositiveImpact &&
              hasNegativeImpact && (
                <div className="absolute transparent top-0 left-0 w-full h-full bg-green-500/10 dark:bg-green-600/20 rounded pointer-events-none z-[1]" />
              )}

            {simFlag && simulation && (
              <DateShiftBadges
                liveStart={safeProject.start_date}
                simStart={safeProject.sim_data.start_date}
                liveEnd={safeProject.end_date}
                simEnd={safeProject.sim_data.end_date}
                organizationWorkingDays={organizationWorkingDays}
                organizationHolidays={organizationHolidays}
              />
            )}

            <div
              style={{
                position: "absolute",
                left: 0,
                bottom: 3,
                width: "100%",
                zIndex: 2,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: `${safeProject.progress || 0}%`,
                  zIndex: 2,
                  height: 3,
                  background: `linear-gradient(90deg, ${activeTab === "dependencies" && hasPositiveImpact ? "#747474" : "#4ade80"}80 0%, ${activeTab === "dependencies" && hasPositiveImpact ? "#747474" : "#4ade80"} 100%)`,
                }}
              />
              <div className="absolute w-full h-[3px] bg-gray-200 dark:bg-[#2A3446]" />
            </div>
            <div className="relative flex flex-col items-start justify-center gap-0.5 select-none px-1 ml-2 py-2 w-full overflow-hidden hover:overflow-visible">
              <div className="flex flex-col items-start justify-center gap-0.5 select-none w-full">
                <div className="flex items-center justify-between w-full">
                  <div
                    className="uppercase text-blue-600 dark:text-[#2E68E8] text-start whitespace-pre"
                    style={{ fontSize: 9 }}
                  >
                    Stage: {safeProject.stage}
                  </div>
                </div>
                <div
                  className="text-gray-900 dark:text-white text-start w-full whitespace-pre inline-flex items-center gap-1"
                  style={{
                    fontSize: 13,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {showRiskTriangle && (
                    <span
                      title={`This is contributing to risk`}
                      className="inline-flex items-center"
                    >
                      <FiAlertTriangle className="text-red-500" />
                    </span>
                  )}

                  {safeProject?.is_sim_pending && (
                    <div className="text-[11px] text-[#467966] dark:text-blue-400 inline-flex items-center gap-1">
                      <FaGlasses className="text-[#467966]" />
                    </div>
                  )}
                  {safeProject.project_name}
                </div>
              </div>
              <div
                className="flex items-center gap-2 w-full whitespace-pre"
                style={{ fontSize: 9, whiteSpace: "nowrap" }}
              >
                <span
                  className="font-semibold "
                  style={{ color: statusHexColor }}
                >
                  {statusLabel}
                  {project?.integration_data?.integration_status &&
                    project.integration_data.integration_status.toLowerCase() !==
                      statusKey &&
                    ` (${project.integration_data.integration_status.charAt(0).toUpperCase() + project.integration_data.integration_status.slice(1).toLowerCase()})`}
                </span>
                <span className="uppercase text-gray-600 dark:text-[#8D929D]">
                  {moment(pStartDate).format("MMM DD")} -{" "}
                  {moment(pEndDate).format("MMM DD")}
                </span>
              </div>

              {/* Custom Tooltip */}
              {showTooltip && (
                <div
                  className={`absolute z-[9999] w-[300px] shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg transform -translate-x-1/2 ${
                    tooltipPosition.placement === "top"
                      ? "bottom-full"
                      : "top-full"
                  }`}
                  style={{
                    left: `${tooltipPosition.x}px`,
                    animation: "fadeIn 0.2s ease-in-out",
                  }}
                  onMouseEnter={() => {
                    setIsTooltipHovered(true);
                    // Clear hide timer when hovering over tooltip
                    if (hideTimerRef.current) {
                      clearTimeout(hideTimerRef.current);
                      hideTimerRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    setIsTooltipHovered(false);
                    // Start hide timer when leaving tooltip
                    hideTimerRef.current = setTimeout(() => {
                      setShowTooltip(false);
                      setIsTooltipHovered(false);
                      hideTimerRef.current = null;
                    }, 200);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {activeTab === "dependencies" &&
                  safeProject.dependencies.length > 0 ? (
                    <>
                      <div className="px-3 py-1.5 rounded-t-lg font-semibold text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-sm text-left">
                        {safeProject.project_name} is getting affected
                      </div>
                      <div className="p-3 text-slate-700 dark:text-slate-200 text-left">
                        <div className="flex flex-col gap-1.5">
                          {impactingDependencies.length > 0 &&
                            impactingDependencies.map((dep, index) => {
                              console.log("Dep name", dep.name)
                              return (
                                <div
                                  key={(dep?.project_id ?? index).toString()}
                                  className="border-l-3 border-red-600 p-1.5 rounded bg-red-50 dark:bg-red-900/40 dark:border-red-500"
                                >
                                  <div className="flex items-start text-red-800 dark:text-red-300 text-xs text-left">
                                    <svg
                                      className="w-3 h-3 mr-1.5 flex-shrink-0 text-red-600 dark:text-red-400 mt-0.5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      ></path>
                                    </svg>
                                    <span className="text-xs leading-tight text-left">
                                      Delayed by
                                      <span className="mx-1 px-1.5 py-0.5 bg-red-200 text-red-900 font-bold rounded text-xs dark:bg-red-700 dark:text-red-100">
                                        {dep?.impact ?? 0}d
                                      </span>
                                      due to
                                      <strong className="ml-1 underline decoration-1 decoration-red-400 text-xs">
                                        {(dep?.name || "").length > 25
                                          ? (dep?.name || "").substring(0, 25) +
                                            "..."
                                          : dep?.name || ""}
                                      </strong>
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          {acceleratingDependencies.length > 0 &&
                            acceleratingDependencies.map((dep, index) => (
                              <div
                                key={(dep?.project_id ?? index).toString()}
                                className="border-l-3 border-green-600 p-1.5 rounded bg-green-50 dark:bg-green-900/40 dark:border-green-500"
                              >
                                <div className="flex items-start text-green-800 dark:text-green-300 text-xs text-left">
                                  <svg
                                    className="w-3 h-3 mr-1.5 flex-shrink-0 text-green-600 dark:text-green-400 mt-0.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M7 10l-2 2 5 5 9-9-2-2-7 7-3-3z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                  <span className="text-xs leading-tight text-left">
                                    Accelerated by
                                    <span className="mx-1 px-1.5 py-0.5 bg-green-200 text-green-900 font-bold rounded text-xs dark:bg-green-700 dark:text-green-100">
                                      {dep?.impact ?? 0}d
                                    </span>
                                    due to
                                    <strong className="ml-1 underline decoration-1 decoration-green-400 text-xs">
                                      {(dep?.name || "").length > 25
                                        ? (dep?.name || "").substring(0, 25) +
                                          "..."
                                        : dep?.name || ""}
                                    </strong>
                                  </span>
                                </div>
                              </div>
                            ))}
                          {impactingDependencies.length === 0 &&
                            acceleratingDependencies.length === 0 && (
                              <div className="border-l-3 border-green-600 p-1.5 rounded bg-green-50 dark:bg-green-900/40 dark:border-green-500">
                                <div className="flex items-center text-green-800 dark:text-green-300 text-xs">
                                  <svg
                                    className="w-3 h-3 mr-1.5 flex-shrink-0 text-green-600 dark:text-green-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                  <span className="text-xs">
                                    No dependency impact
                                  </span>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <EntityViewTooltip
                      projectData={safeProject}
                      isSimulation={simFlag}
                      tooltipType="project"
                    />
                  )}

                  {/* Tooltip Arrow */}
                  <div
                    className={`absolute w-3 h-3 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 transform left-1/2 -translate-x-1/2 ${
                      tooltipPosition.placement === "top"
                        ? "rotate-45 border-r border-b top-full -mt-1.5"
                        : "rotate-45 border-l border-t bottom-full -mb-1.5"
                    }`}
                  />
                </div>
              )}
            </div>

            {simFlag &&
              userRole === "admin" &&
              showCost &&
              (safeProject.project_cost > 0 ||
                safeProject.sim_data.project_cost > 0) && (
                <div
                  className={`absolute right-2 bottom-[-2px] rounded text-white 
                  ${safeProject.project_cost >= safeProject.sim_data.project_cost ? "bg-green-600 dark:bg-green-700" : "bg-red-600 dark:bg-red-700"}
                `}
                  style={{
                    height: 22,
                    padding: "2px 4px",
                    fontSize: 12,
                    width: "100% !important",
                    zIndex: 10,
                  }}
                  title={`Live : $${numberWithCommas(safeProject.project_cost)} \nSimulation : $${numberWithCommas(safeProject.sim_data.project_cost)}`}
                  onMouseEnter={() => {
                    // Clear hide timer when hovering over simFlag badge
                    if (hideTimerRef.current) {
                      clearTimeout(hideTimerRef.current);
                      hideTimerRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    // Start hide timer when leaving simFlag badge
                    hideTimerRef.current = setTimeout(() => {
                      setShowTooltip(false);
                      setIsTooltipHovered(false);
                      hideTimerRef.current = null;
                    }, 200);
                  }}
                >
                  {"$"}
                </div>
              )}

            {/* Dependency Chips */}
            {activeTab === "dependencies" &&
              safeProject.dependencies.length > 0 && (
                <div
                  className="dependency-chips hover:!overflow-visible"
                  style={{
                    position: "absolute",
                    left: 2,
                    bottom: -28,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    overflow: showTooltip ? "visible" : "hidden",
                    whiteSpace: "nowrap",
                    background: "transparent",
                    boxShadow: "0 0 4px rgba(0,0,0,0.08),",
                    zIndex: 10,
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => {
                    // Clear hide timer when hovering over dependency chips
                    if (hideTimerRef.current) {
                      clearTimeout(hideTimerRef.current);
                      hideTimerRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    // Start hide timer when leaving dependency chips area
                    hideTimerRef.current = setTimeout(() => {
                      setShowTooltip(false);
                      setIsTooltipHovered(false);
                      hideTimerRef.current = null;
                    }, 200);
                  }}
                >
                  <span
                    className="text-gray-600 dark:text-gray-400"
                    style={{
                      fontSize: 12,
                      paddingLeft: 6,
                      paddingRight: 7,
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    }}
                  >
                    Dependency on
                  </span>
                  <div>
                    {safeProject.dependencies.map((dep: any, i: number) => {
                      const maxLen = 18;
                      const name = dep?.name || dep?.label || "";
                      const depId =
                        dep?.id || dep?.to || dep?.project_to || "";
                      const truncated =
                        name.length > maxLen
                          ? name.slice(0, maxLen) + "..."
                          : name;
                      return (
                        <span
                          key={(depId || i).toString()}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(resetProjectIdForScroll());
                            setTimeout(() => {
                              dispatch(
                                setProjectIdForScroll(`project-${depId}`),
                              );
                            }, 500);
                          }}
                          onMouseEnter={() => {
                            // Clear hide timer when hovering over individual dependency chip
                            if (hideTimerRef.current) {
                              clearTimeout(hideTimerRef.current);
                              hideTimerRef.current = null;
                            }
                          }}
                          className="bg-gray-100 dark:bg-[#1B263C] text-blue-600 dark:text-[#2563eb] hover:bg-gray-200 dark:hover:bg-[#212e47] transition-colors"
                          style={{
                            display: "inline-block",
                            borderRadius: 6,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            padding: "2px 10px",
                            fontSize: 12,
                            overflow: "visible",
                            textOverflow: "clip",
                            whiteSpace: "nowrap",
                            paddingLeft: 4,
                            paddingRight: 4,
                            verticalAlign: "middle",
                            cursor: "pointer",
                          }}
                        >
                          <span
                            title={name}
                            style={{
                              overflow: "visible",
                              textOverflow: "clip",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {truncated}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        </button>
      </div>

      {/* Subtle swept-area cue for Dependencies tab shift (animated with progress) */}
      {activeTab === "dependencies" && Math.abs(dependencyShiftPx) > 0 && (
        <div
          className="absolute rounded-sm"
          style={{
            top: index * GT_taskCardHeight,
            left: Math.min(
              taskPosLeftInPx,
              taskPosLeftInPx + dependencyShiftPx * depSweepProgress,
            ),
            width: Math.abs(dependencyShiftPx * depSweepProgress),
            height: GT_taskCardHeight - GT_progressBarAndTasksGapInPx,
            backgroundColor: "rgba(148,163,184,0.10)", // slate-400 @10%
            borderLeft: "1px dashed rgba(148,163,184,0.25)",
            borderRight: "1px dashed rgba(148,163,184,0.25)",
            zIndex: 3,
            pointerEvents: "none",
          }}
          aria-hidden
        />
      )}

      {dependecyView &&
        safeProject.dependencies &&
        safeProject.dependencies.length > 0 &&
        safeProject.dependencies.map((dependency: any, i: number) => {
          const depId = dependency?.id;
          const dependencyElement = document.getElementById(`project-${depId}`);
          const dependencyWidth = dependencyElement
            ? dependencyElement.offsetWidth
            : 0;
          const impactValue = getDependencyImpactValue(dependency, simFlag);
          const arrowColor = getImpactColorFromNumber(impactValue);

          return (
            <Xarrow
              key={uuid()}
              path={"smooth"}
              start={`project-${depId}`}
              end={`project-${safeProject.project_id}`}
              startAnchor={{
                position: "bottom",
                offset: { x: -(dependencyWidth / 2) + 8, y: -1 },
              }}
              endAnchor={{ position: "left", offset: { y: 0 } }}
              headSize={6}
              strokeWidth={2}
              color={arrowColor}
              dashness={{ strokeLen: 5, nonStrokeLen: 3 }}
              zIndex={isProjectHovered ? 5 : 1}
              animateDrawing={false}
              showHead={true}
              tailSize={0}
              curveness={curveness}
            />
          );
        })}
      <style>
        {`
          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateX(-50%) translateY(${tooltipPosition.placement === "top" ? "5px" : "-5px"}); 
            }
            to { 
              opacity: 1; 
              transform: translateX(-50%) translateY(0); 
            }
          }
        `}
      </style>
    </>
  );
};

export default GTStrategyMap;
